/**
 * Bulk-export download plumbing for the Profiler search-results page, extracted
 * from the `onDownloadProfilerSearchClick` store action in
 * SearchResults/index.jsx (TD-2746 — SonarCloud S2004).
 *
 * The action's `onSuccess` wrapped an async IIFE, so the `runInAction` blocks
 * inside it (and inside its `catch`) sat six closures deep against a nesting
 * limit of 5. Fetching the presigned URL and saving the blob now happens here.
 *
 * The same extraction retires four copies of the
 * `if (isOnepager) … else if (isContact) … else if (isProfiles)` ladder that set
 * the per-type progress flags: `PROFILER_DOWNLOAD_TYPES` names the flags once and
 * `setProfilerDownloadFlags` writes them.
 */
import { runInAction } from 'mobx';
import { extractFilename } from '../components/profiler/CompanyDetails/utils/downloadUtils';

/**
 * Per-export-type metadata: the two `state` flags that drive the spinner and the
 * success tick, the extension used by the fallback filename, the noun used in
 * the failure message, and whether the download confirmation must ask for the
 * contacts price (`precheckIncludesContacts`).
 */
export const PROFILER_DOWNLOAD_TYPES = {
  onepager: {
    downloadingKey: 'isDownloadingProfilerSearchOnepagerList',
    successKey: 'isSuccessDownloadProfilerSearchOnepagerList',
    extension: '.pptx',
    failureLabel: 'onepager list'
  },
  contact: {
    downloadingKey: 'isDownloadingProfilerSearchContactList',
    successKey: 'isSuccessDownloadProfilerSearchContactList',
    extension: '.xlsx',
    failureLabel: 'contact list',
    precheckIncludesContacts: true
  },
  profiles: {
    downloadingKey: 'isDownloadingProfilerSearchProfilesList',
    successKey: 'isSuccessDownloadProfilerSearchProfilesList',
    extension: '.xlsx',
    failureLabel: 'company profiles'
  }
};

/**
 * True when the confirmation for this export type must send `includeContacts`
 * to /bulk-download-precheck. The contact list holds only companies with at
 * least one contact, and the BE charges only those, so it has to be quoted on
 * the same set. Any other or unknown type (including no pending export) is
 * priced on the whole source.
 */
export const precheckIncludesContacts = (type) =>
  !!PROFILER_DOWNLOAD_TYPES[type]?.precheckIncludesContacts;

/**
 * Writes the `downloading` / `success` flags for one export type. Only the keys
 * actually passed are written — starting a download must not clear the success
 * tick left by the previous one, which is what the original ladders did. Unknown
 * types are ignored, matching their implicit else.
 */
export const setProfilerDownloadFlags = (state, type, { downloading, success } = {}) => {
  const config = PROFILER_DOWNLOAD_TYPES[type];
  if (!config) return;
  if (downloading !== undefined) state[config.downloadingKey] = downloading;
  if (success !== undefined) state[config.successKey] = success;
};

/**
 * True while any of the three exports is in flight. Drives the mobile bar's
 * in-progress state: once the Download sheet closes on selection, its
 * per-entry icons are no longer on screen. Success is deliberately not surfaced
 * there — the success flags are never cleared, so the bar would stay "done" for
 * the rest of the visit.
 */
export const isAnyProfilerDownloadInFlight = (state) =>
  Object.values(PROFILER_DOWNLOAD_TYPES).some(({ downloadingKey }) => !!state[downloadingKey]);

/**
 * Filename for the saved export: the one the BE put in `Content-Disposition`,
 * else `td-<project>-<type>-list.<ext>`.
 */
export const buildProfilerExportFilename = ({ contentDisposition, projectName, type }) => {
  const extension = PROFILER_DOWNLOAD_TYPES[type]?.extension || '.xlsx';
  return extractFilename(contentDisposition, `td-${projectName}-${type}-list${extension}`);
};

/**
 * Hands the fetched blob to the browser.
 *
 * Deliberately NOT the shared `triggerDownload` from `downloadUtils`: that one
 * also sets `target="_blank"` and attaches the link to the document. Both are
 * arguably improvements, but this ticket is behaviour-preserving — unifying the
 * two is a follow-up.
 */
const saveBlob = (blob, filename) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Fetches a presigned export URL and saves it, flipping the per-type flags on the
 * way through. Resolves once the download has been handed to the browser; never
 * rejects — a failure is reported through the flags, as before.
 */
export const saveProfilerExportFile = async ({ url, type, state, projectName }) => {
  try {
    const fileResponse = await fetch(url);
    const blob = await fileResponse.blob();

    if (!state.isRendered) {
      return;
    }

    runInAction(() => {
      setProfilerDownloadFlags(state, type, { downloading: false, success: true });
    });

    saveBlob(
      blob,
      buildProfilerExportFilename({
        contentDisposition: fileResponse.headers.get('Content-Disposition'),
        projectName,
        type
      })
    );
  } catch (err) {
    console.error(err);
    runInAction(() => {
      setProfilerDownloadFlags(state, type, { downloading: false, success: false });
    });
  }
};
